/**
 * ReviewerLayout — chrome for the Review User (nabs_reviewer) surface.
 *
 * The reviewer is org-less NABS staff, so the tenant Navbar (org-scoped
 * widgets) and the admin sidebar (super_admin surfaces) are both wrong
 * for them. This layout gives their two pages a proper sidebar + header:
 *   Sidebar — Review Dashboard, My Profile.
 *   Header  — page context on the left; a round profile avatar on the
 *             top-right (photo when set, initials fallback) opening a
 *             dropdown with My Profile + Logout.
 *
 * Profile data comes from GET /users/me (made org-less-safe on the BE).
 * The avatar refreshes after the profile page saves a new image via the
 * shared "reviewer-profile-updated" window event — cheap, no context
 * provider needed for two pages.
 */
import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Avatar, Button, Dropdown, Layout, Menu, Result, Typography } from "antd";
import {
  DashboardOutlined,
  FileTextOutlined,
  LogoutOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { fetchData } from "../api/apiService";
import { USERS } from "../api/endpointMap";
import { useAuth } from "../context/AuthContext";
import { isReviewModeEnabled } from "../utils/reviewMode";
import { PORTAL_PAGES } from "../pages/review/portals";

const { Sider, Header, Content } = Layout;
const { Text } = Typography;

const initialsOf = (me) => {
  const name = me?.full_name || me?.email || "";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return String(name).slice(0, 2).toUpperCase() || "RU";
};

export default function ReviewerLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [me, setMe] = useState(null);

  const loadMe = useCallback(() => {
    fetchData(USERS.USER_INFO)
      .then((data) => setMe(data || null))
      .catch(() => setMe(null));
  }, []);

  useEffect(() => {
    loadMe();
    // Profile page dispatches this after saving name / photo so the
    // header avatar updates without a full reload.
    window.addEventListener("reviewer-profile-updated", loadMe);
    return () => window.removeEventListener("reviewer-profile-updated", loadMe);
  }, [loadMe]);

  // Review Mode feature switch (System Configuration). The backend
  // already refuses reviewer sign-in and 403s every reviewer route
  // while OFF; this is the explanation a super admin (or a reviewer
  // whose session predates the switch) sees instead of a broken page.
  if (!isReviewModeEnabled()) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Result
          status="info"
          title="Review Mode is turned off"
          subTitle="A super admin can enable it from System Configuration under the Review Mode tab."
          extra={
            <Button type="primary" onClick={logout}>
              Sign out
            </Button>
          }
        />
      </div>
    );
  }

  // Per-portal pages (04-10-2026): only the portals this reviewer handles are
  // offered - requests of a portal they do not hold never reach them anyway.
  // The flags come from the cached login payload; unknown = show all three.
  // Portals come from GET /users/me (server truth), NOT the cached login
  // payload: an administrator can change a reviewer's portals while they are
  // signed in, and the cache would keep showing the old ones (05-10-2026).
  // Until /me answers, fall back to the cache, then to nothing.
  const portalSource = me || user || null;
  const portalPages = portalSource
    ? PORTAL_PAGES.filter((p) =>
        Boolean(
          portalSource[
            {
              income_tax: "enable_income_tax_portal",
              gst: "enable_gst_portal",
              tds: "enable_tds_portal",
            }[p.portal]
          ],
        ),
      )
    : [];

  const portalPage = portalPages.find(
    (p) => location.pathname === `/review-dashboard/${p.slug}`,
  );
  const selectedKey = location.pathname.startsWith("/reviewer-profile")
    ? "profile"
    : portalPage
      ? `portal-${portalPage.slug}`
      : "dashboard";

  const menuItems = [
    {
      key: "dashboard",
      icon: <DashboardOutlined />,
      label: <NavLink to="/review-dashboard">Review Dashboard</NavLink>,
    },
    ...portalPages.map((p) => ({
      key: `portal-${p.slug}`,
      icon: <FileTextOutlined />,
      label: <NavLink to={`/review-dashboard/${p.slug}`}>{p.label}</NavLink>,
    })),
    {
      key: "profile",
      icon: <UserOutlined />,
      label: <NavLink to="/reviewer-profile">My Profile</NavLink>,
    },
  ];

  const avatarMenu = {
    items: [
      {
        key: "profile",
        icon: <UserOutlined />,
        label: "My Profile",
        onClick: () => navigate("/reviewer-profile"),
      },
      { type: "divider" },
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: "Logout",
        onClick: logout,
      },
    ],
  };

  return (
    <Layout className="min-h-screen">
      <Sider
        theme="light"
        width={240}
        breakpoint="lg"
        collapsedWidth={72}
        className="border-r border-slate-200"
      >
        <div className="h-16 flex items-center justify-center border-b border-slate-100">
          <Text strong className="text-lg" style={{ color: "#1e3a8a" }}>
            NoticeAI Review
          </Text>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          className="border-r-0"
        />
      </Sider>

      <Layout>
        <Header
          className="flex items-center justify-between px-6"
          style={{
            background: "#fff",
            borderBottom: "1px solid #e2e8f0",
            height: 64,
          }}
        >
          <Text type="secondary">
            Reviewer workspace
            {me?.full_name ? ` : ${me.full_name}` : ""}
          </Text>
          <Dropdown menu={avatarMenu} placement="bottomRight" trigger={["click"]}>
            <Avatar
              size={40}
              src={me?.profile_image_url || undefined}
              style={{
                backgroundColor: "#1e3a8a",
                cursor: "pointer",
                border: "2px solid #e2e8f0",
              }}
            >
              {initialsOf(me)}
            </Avatar>
          </Dropdown>
        </Header>
        <Content className="bg-slate-50 p-6">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
